<template>
	<div class="card-row-container" v-if="!isRowMultiline">
		<div
			v-for="box in rowTemplate"
			class="card-cell"
			:key="box.id"
			:class="[isImageBox(box) && !isTemplateMode ? 'mr-3' : `flex-${box.grow}`]"
		>
			<div class="card-field-title" v-if="titleExists(box.id)">
				{{ field(box.id).title.value }}
			</div>
			<CardRowImage v-if="isImageBox(box)" :src="getBoxValue(box)" :isTemplateMode="isTemplateMode" />
			<template v-else>
				<div v-if="field(box.id) && field(box.id).value" class="card-field-data" :title="field(box.id).value">
					{{ field(box.id).value }}
				</div>
				<div v-else-if="isTemplateMode" class="dummy-filler" :class="{ 'is-highlight': box.id === highlightFieldIndex }"><div></div></div>
			</template>
		</div>
	</div>
	<div
		v-else
		class="card-row-container is-multiline"
		:class="{ 'is-expanded': isExpanded, 'force-show-more': forceShowMore, 'has-image': hasImageInRow }"
	>
		<div v-for="box in rowTemplate" :key="box.id">
			<CardRowImage class="image-placement" v-if="isImageBox(box)" :src="getBoxValue(box)" :isTemplateMode="isTemplateMode" />
			<div v-else class="card-cell">
				<div ref="multilineRef" class="card-field-data" v-if="getBoxValue(box)">
					<div v-for="(paragraph, i) in getLongTextByParagraph(box)" :key="i">
						<span v-html="sanitizeHtml(paragraph)" />
						<br v-if="i + 1 !== getLongTextByParagraph(box).length" />
					</div>
				</div>
				<div v-else-if="isTemplateMode" class="multiline-dummy-filler">
					<div></div>
					<div class="flex-centerize show-more-indicator">
						<template v-if="!forceShowMore"> show more </template>
					</div>
				</div>
			</div>
			<div class="expand-btn" v-if="hasRefs && isCardOverflow && !forceShowMore" @click.prevent.stop="isExpanded = !isExpanded">
				{{ isExpanded ? 'Show less' : 'Show more' }}
			</div>
		</div>
	</div>
</template>

<script>
import CardRowImage from './CardRowImage';
import { sanitizeHtml } from '@/utils/helpers';
export default {
	name: 'CardRow',
	props: {
		cardData: { type: Object },
		rowTemplate: { type: Array },
		isTemplateMode: { type: Boolean },
		highlightFieldIndex: { type: Number },
	},
	components: {
		CardRowImage,
	},
	computed: {
		firstField() {
			return this.field(this.rowTemplate[0].id) || {};
		},
		isRowMultiline() {
			return this.rowTemplate.some((box) => box.isMultiline);
		},
		forceShowMore() {
			return this.rowTemplate.some((box) => box.forceShowMore);
		},
		isCardOverflow() {
			let element = this.$refs.multilineRef || {};
			if (element?.length === 1) {
				// because it's in v-for. leaving it as original for backwards compatibility
				element = element[0];
			}
			return element && element.offsetHeight >= element.scrollHeight;
		},
		hasImageInRow() {
			const { isImageBox } = this;
			return this.rowTemplate.some(isImageBox);
		},
	},
	methods: {
		field(id) {
			return this.cardData.fields[id];
		},
		titleExists(id) {
			const field = this.field(id);
			return field && field.title && field.title.enabled && field.title.value;
		},
		isImageBox(box) {
			return box.isImage;
		},
		getBoxValue(box) {
			return this.field(box.id)?.value;
		},
		sanitizeHtml(html) {
			return sanitizeHtml(html);
		},
		getLongTextByParagraph(box) {
			return this.getBoxValue(box).split('\n');
		},
	},
	mounted() {
		this.hasRefs = Object.keys(this.$refs).length > 0;
	},
	data() {
		return {
			hasRefs: false,
			isExpanded: false,
			showImagePreview: this.isTemplateMode,
		};
	},
};
</script>

<style scoped lang="scss">
.card-row-container {
	position: relative;
	width: 100%;
	padding: 4px 10px;

	&:not(.is-multiline) {
		display: flex;
		justify-content: space-between;
		align-items: center;
		flex-direction: row;

		.card-cell {
			display: flex;
			align-items: center;
			height: 40px;
		}
	}
	&.is-multiline {
		&.has-image {
			padding: 4px 10px 0 50px;
		}
		.card-cell {
			min-height: 40px;
			max-height: 80px;
			width: 100%;
			overflow: hidden;
		}
	}
	.card-cell {
		position: relative;

		.card-field-title {
			position: absolute;
			top: 0;
			font-size: 10px;
			font-weight: 300;
			left: 0;
			color: #969696;
		}
	}

	.card-cell,
	.card-field-data {
		max-width: 100%;
		padding-right: 5px;
		font-size: 14px;
	}
	.is-multiline .card-field-data {
		max-height: 100%;
		overflow: hidden;
	}
	.card-field-data {
		overflow: hidden;
	}
	.card-field-data:not(.is-multiline) {
		overflow: hidden;
		max-height: 100%;
		text-overflow: ellipsis;
	}

	.expand-btn {
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		padding: 4px 0;
		border-top: 1px solid var(--button-border);
		border-bottom-left-radius: 8px;
		border-bottom-right-radius: 8px;
		background-color: var(--bg-color);
		text-align: center;

		&:hover {
			filter: brightness(0.95);
			transition: 0.3s;
		}
	}
	&.is-expanded {
		.card-cell {
			height: calc(100%);
			max-height: calc(100%);
			padding-bottom: 60px;
		}
	}
	&.force-show-more {
		.card-cell {
			height: calc(100%);
			max-height: calc(100%);
		}
	}
}

.dummy-filler {
	width: 100%;
	padding: 5px;

	> div {
		height: 10px;
		border-radius: 20px;
		background-color: #e6e7ec;
	}
	&.is-highlight > div {
		border: 1px solid #5c8df5;
		background-color: #afd9f8;
	}
}

.multiline-dummy-filler {
	width: 100%;
	padding: 7px 5px;
	border-radius: 10px;
	background-color: #e6e7ec;

	> div {
		height: 10px;
		margin: 0 5px 0 5px;
		border-radius: 20px;
		background-color: #bebebf;

		&:first-child {
			margin-bottom: 10px;
		}
	}
}
.image-placement {
	position: absolute;
	top: 8px;
	left: 10px;
}
.show-more-indicator {
	color: #4d4d4dbe;
}
</style>
